import React, { FC } from "react";
import { AppPage } from "shared-ts-ui-app-page";
import {
  Box,
  Card,
  Container,
  Link,
  List,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { createLazyRoute, Link as RouterLink } from "@tanstack/react-router";
import LocalLibraryRoundedIcon from "@mui/icons-material/LocalLibraryRounded";
import { ListItem } from "./ListItem";
import { TipsIcon } from "./TipsIcon";
import { EnvironmentIcon } from "./EnvironmentIcon";
import { SavingEnergyIcon } from "./SavingEnergyIcon";
import { ActionIcon } from "./ActionIcon";
import { WarningIcon } from "./WarningIcon";

export const GuidancePage: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation("my-chimney-ts-translations");
  return (
    <AppPage
      $key={"GuidancePage"}
      name={t("Guide")}
      icon={LocalLibraryRoundedIcon}
      mainPaneProps={{
        scrollableContentVertically: true,
      }}
      mainPane={
        <Container
          sx={{
            ul: {
              paddingInlineStart: theme.spacing(3),
            },
            ".MuiTypography-h1": {
              fontSize: "1rem",
              mt: 2,
              mb: 2,
            },
            ".MuiTypography-h2": {
              fontSize: "2.5rem",
              fontWeight: 400,
              mt: 2,
              mb: 2,
            },
            ".MuiTypography-h3": {
              fontSize: "2rem",
              fontWeight: 400,
              mt: 3,
              mb: 1,
            },
            ".MuiTypography-body1": {
              m: 0.5,
            },
            ".MuiAlert-root": {
              mt: 1,
              mb: 1,
              ml: 2,
              mr: 1,
            },
          }}
        >
          <Card sx={{ p: 2 }}>
            <Typography variant={"h2"} id={"ignition"}>
              {t("Ignition")}
            </Typography>
            <Typography>{t("Guide.This is general advice...")}</Typography>
            <Stack
              direction={"column"}
              useFlexGap
              gap={2}
              sx={{
                pt: 1,
                pl: 1,
                pr: 1,
                "&& .MuiTypography-h3": { mt: 0 },
                "&& ul": { paddingInlineStart: 1 },
                "& ul>li:last-child": { pb: 0 },
              }}
            >
              <Box id={"use-dry-wood"}>
                <Box>
                  <Typography variant={"h3"}>
                    {t("Guide.Use dry wood")}
                  </Typography>
                  <ul>
                    <ListItem icon={<EnvironmentIcon />}>
                      {t("Guide.Use dry wood..1")}
                    </ListItem>
                    <ListItem icon={<SavingEnergyIcon />}>
                      {t("Guide.Use dry wood..2")}
                    </ListItem>
                    <ListItem icon={<TipsIcon />}>
                      {t("Guide.Use dry wood..3")}
                    </ListItem>
                  </ul>
                </Box>
              </Box>
              <Box id={"ensure-good-access-to-air"}>
                <Box>
                  <Typography variant={"h3"}>
                    {t("Guide.Ensure good access to air")}
                  </Typography>
                  <ul>
                    <ListItem icon={<EnvironmentIcon />}>
                      {t("Guide.Ensure good access to air..1")}
                    </ListItem>
                    <ListItem icon={<ActionIcon />}>
                      {t("Guide.Ensure good access to air..2")}
                    </ListItem>
                    <ListItem icon={<TipsIcon />}>
                      {t("Guide.Ensure good access to air..3")}
                    </ListItem>
                    <ListItem icon={<WarningIcon />}>
                      {t("Guide.Ensure good access to air..4")}
                    </ListItem>
                  </ul>
                </Box>
              </Box>
              <Box id={"build-the-fire-correctly"}>
                <Box>
                  <Typography variant={"h3"}>
                    {t("Guide.Build the fire correctly")}
                  </Typography>
                  <ul>
                    <ListItem icon={<ActionIcon />}>
                      {t("Guide.Build the fire correctly..1")}
                    </ListItem>
                    <ListItem icon={<TipsIcon />}>
                      {t("Guide.Build the fire correctly..2")}
                    </ListItem>
                    <ListItem icon={<WarningIcon />}>
                      {t("Guide.Build the fire correctly..3")}
                    </ListItem>
                    <ListItem icon={<WarningIcon />}>
                      {t("Guide.Build the fire correctly..4")}
                    </ListItem>
                  </ul>
                </Box>
              </Box>
              <Box>
                <Box>
                  <Typography variant={"h3"} id={"light-up-from-the-top"}>
                    {t("Guide.Light up from the top")}
                  </Typography>
                  <ul>
                    <ListItem icon={<EnvironmentIcon />}>
                      {t("Guide.Light up from the top..1")}
                    </ListItem>
                    <ListItem icon={<TipsIcon />}>
                      {t("Guide.Light up from the top..2")}
                    </ListItem>
                  </ul>
                </Box>
              </Box>
            </Stack>
          </Card>
        </Container>
      }
      supportingPaneProps={{
        label: t("Table of Contents"),
        scrollableContentVertically: true,
        resizePanelProps: {
          minWidth: 300,
          maxWidth: 400,
          vertical: {
            maxHeight: 420,
          },
          mobile: {
            maxHeight: 420,
          },
        },
      }}
      supportingPane={
        <Card sx={{ flexGrow: 1, p: 2 }}>
          <Box
            sx={{
              a: {
                textDecoration: "none",
              },
              ul: {
                paddingInlineStart: theme.spacing(3),
              },
              "ul > li > a": {
                color: "text.primary",
              },
            }}
          >
            <ul>
              <Typography component={"li"}>
                <Link component={RouterLink} to={"#ignition"}>
                  {t("Ignition")}
                </Link>
              </Typography>
              <ul>
                <Typography component={"li"}>
                  <Link component={RouterLink} to={"#use-dry-wood"}>
                    {t("Guide.Use dry wood")}
                  </Link>
                </Typography>
                <Typography component={"li"}>
                  <Link
                    component={RouterLink}
                    to={"#ensure-good-access-to-air"}
                  >
                    {t("Guide.Ensure good access to air")}
                  </Link>
                </Typography>
                <Typography component={"li"}>
                  <Link component={RouterLink} to={"#build-the-fire-correctly"}>
                    {t("Guide.Build the fire correctly")}
                  </Link>
                </Typography>
                <Typography component={"li"}>
                  <Link component={RouterLink} to={"#light-up-from-the-top"}>
                    {t("Guide.Light up from the top")}
                  </Link>
                </Typography>
              </ul>
            </ul>
            <List dense subheader={t("Legend") + ": "}>
              <ListItem icon={<EnvironmentIcon />}>
                {t("Guide.Good for the environment")}
              </ListItem>
              <ListItem icon={<SavingEnergyIcon />}>
                {t("Guide.Good for the environment")}
              </ListItem>
              <ListItem icon={<TipsIcon />}>{t("Guide.Tips")}</ListItem>
              <ListItem icon={<WarningIcon />}>{t("Guide.Warning")}</ListItem>
              <ListItem icon={<ActionIcon />}>{t("Guide.Action")}</ListItem>
            </List>
          </Box>
        </Card>
      }
    />
  );
};

export const GuidancePageRoute = createLazyRoute("/guidance")({
  component: GuidancePage,
});
